import React from "react";
import {
  X,
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  Camera,
  MapPin,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatRelativeTime } from "@/lib/utils";

interface OrphanEvent {
  id: number;
  plate: string;
  timestamp: string;
  camera_id: string;
  confidence: number | null;
  state: string;
  location: string;
  zone: string;
  hours_orphaned: number;
  type: "entry" | "exit";
}

interface ProblemPlateDetailsProps {
  plate: string | null;
  isOpen: boolean;
  onClose: () => void;
  entries: OrphanEvent[];
  exits: OrphanEvent[];
  totalOrphans: number;
}

export function ProblemPlateDetailsModal({
  plate,
  isOpen,
  onClose,
  entries,
  exits,
  totalOrphans,
}: ProblemPlateDetailsProps) {
  if (!plate) return null;

  const renderEvent = (event: OrphanEvent) => {
    const isEntry = event.type === "entry";
    const Icon = isEntry ? ArrowDownCircle : ArrowUpCircle;
    const iconColor = isEntry ? "text-green-600" : "text-blue-600";
    const bgColor = isEntry
      ? "bg-green-50 dark:bg-green-950/20"
      : "bg-blue-50 dark:bg-blue-950/20";
    const borderColor = isEntry
      ? "border-green-200 dark:border-green-800"
      : "border-blue-200 dark:border-blue-800";

    return (
      <div
        key={event.id}
        className={`p-4 rounded-lg border ${bgColor} ${borderColor} mb-3`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <Icon className={`h-5 w-5 ${iconColor} mt-0.5`} />
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="font-semibold text-sm">
                  {isEntry ? "Entry" : "Exit"}
                </span>
                <span className="text-sm text-muted-foreground">
                  {new Date(event.timestamp).toLocaleString()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {event.location} - {event.zone}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <span>Camera {event.camera_id}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="text-xs">
                    {event.state}
                  </Badge>
                  {event.confidence && (
                    <span className="text-xs text-muted-foreground">
                      {(event.confidence * 100).toFixed(1)}% confidence
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-600 font-medium">
                    Orphaned {event.hours_orphaned}h
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
            <span>Problem Plate Details</span>
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center space-x-4 mt-2">
              <span className="font-mono text-2xl font-bold text-foreground">
                {plate}
              </span>
              <Badge variant="destructive">
                {totalOrphans} orphaned events
              </Badge>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Orphaned Entries */}
          {entries.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <ArrowDownCircle className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-lg">
                  Orphaned Entries ({entries.length})
                </h3>
              </div>
              <div className="space-y-2">{entries.map(renderEvent)}</div>
            </div>
          )}

          {/* Orphaned Exits */}
          {exits.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <ArrowUpCircle className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-lg">
                  Orphaned Exits ({exits.length})
                </h3>
              </div>
              <div className="space-y-2">{exits.map(renderEvent)}</div>
            </div>
          )}

          {/* No orphans message */}
          {entries.length === 0 && exits.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-green-600" />
              <p>
                No orphaned events found for this plate in the selected date
                range.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2 flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4" />
            <span>Investigation Tips</span>
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>
              Multiple orphaned entries suggest camera misreads or damaged
              plates
            </li>
            <li>
              Check if plate contains confusable characters (O/0, I/1, etc.)
            </li>
            <li>
              Verify camera angles and lighting at the specified locations
            </li>
            <li>Consider if this is a vanity plate with unusual formatting</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
